import type { FC } from 'react';
import { useEffect } from 'react';
import { InfoBox } from '@THC/components/InfoBox';
import { MDText } from 'i18n-react';

import { useVehiclePhotosByKind } from 'Components/Gallery';
import { ServiceRecordValues } from 'Pages/vehicles/schemas/serviceHistory.schema';
import { logger } from 'Services/logger/logger';

import { ImagesPreview } from './ImagesPreview';
import { SERVICE_HISTORY_AGE_LIMIT } from './ServiceHistoryEnhancedSection.consts';
import texts from './ServiceHistoryEnhancedSection.texts.json';
import { ServiceHistoryError } from './ServiceHistoryError';
import { ServiceHistoryInfoCards } from './ServiceHistoryInfoCards';
import { ServiceHistoryItems } from './ServiceHistoryItems';
import { useServiceHistory } from './useServiceHistory';
import { useShouldHideDetailedServiceHistoryCards } from './useShouldHideDetailedServiceHistoryCards';

import styles from './ServiceHistoryEnhancedSection.module.scss';

const LocalT = new MDText(texts);

export const ServiceHistoryEnhancedSection: FC = () => {
	const images = useVehiclePhotosByKind(['docs_service_history']);
	const {
		distanceInterval,
		id,
		independentServiceStampsCount,
		mileage,
		officialServiceStampsCount,
		serviceHistoryRecords,
		serviceRecord,
		timeInterval,
		vehicleAge,
	} = useServiceHistory();
	const shouldHideDetailedServiceHistoryCards = useShouldHideDetailedServiceHistoryCards();

	const isNewishVehicle = vehicleAge <= SERVICE_HISTORY_AGE_LIMIT;
	const hasApiError = serviceHistoryRecords === null;
	const hasNoServiceHistory = [
		ServiceRecordValues.NOT_APPLICABLE,
		ServiceRecordValues.NO,
		ServiceRecordValues.NOT_PROVIDED,
	].includes(serviceRecord);
	const hasServiceDocs = !hasNoServiceHistory && !!images.length;
	const shouldShowRecords = isNewishVehicle && !hasNoServiceHistory;
	const hasContentToShow = hasApiError || shouldShowRecords || hasServiceDocs;

	useEffect(() => {
		if (hasApiError) {
			logger.error({
				context: { enquiryId: id },
				message: 'Error fetching service history',
				scope: 'ServiceHistoryEnhancedSection',
			});
		}
	}, [hasApiError, id]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				{!hasNoServiceHistory && !shouldHideDetailedServiceHistoryCards && (
					<InfoBox
						content={LocalT.translate(isNewishVehicle ? 'badge.text' : 'badge.text5Plus')}
						heading={LocalT.translate('badge.title')}
						variant="info"
					/>
				)}
				<ServiceHistoryInfoCards
					distanceInterval={distanceInterval}
					hasNoServiceHistory={hasNoServiceHistory}
					independentServiceStampsCount={independentServiceStampsCount}
					isNewishVehicle={isNewishVehicle}
					mileage={mileage}
					officialServiceStampsCount={officialServiceStampsCount}
					serviceRecord={serviceRecord}
					serviceRecords={serviceHistoryRecords}
					shouldHideDetailedServiceHistoryCards={shouldHideDetailedServiceHistoryCards}
					timeInterval={timeInterval}
					vehicleAge={vehicleAge}
				/>
			</div>
			{hasContentToShow && (
				<>
					<div className={styles.divider} />
					<div className={styles.content}>
						{hasApiError && <ServiceHistoryError />}
						{shouldShowRecords && <ServiceHistoryItems serviceHistoryRecords={serviceHistoryRecords} />}
						{hasServiceDocs && <ImagesPreview images={images} />}
					</div>
				</>
			)}
		</div>
	);
};
